// Vocabulary the equation parser accepts, plus the display metadata the
// builder derives from it. Kept together because these travel as a set:
// changing what the parser allows means changing what the editor
// highlights and what the unit dropdowns offer.

// Mirror of `_DEFAULT_EMISSION_SPECIES` in core/pathway/builder.py.
export const EMISSION_SPECIES = new Set(['voc', 'co', 'co2', 'co2_equivalent', 'ch4', 'n2o', 'sox', 'nox']);

// Curated units accepted by the equation parser (see UNIT_TOKENS in
// core/pathway/equation_parser.py). Must stay in sync with the backend.
export const PARAM_UNITS = [
  // Dimensionless
  { value: 'scalar', label: 'scalar' },
  { value: '%', label: '% (0–1)' },
  { value: 'kg/kg', label: 'kg/kg' },
  { value: 'g/kg', label: 'g/kg' },
  // Temperature / pressure
  { value: 'degC', label: '°C' },
  { value: 'K', label: 'K' },
  { value: 'bar', label: 'bar' },
  // Mass
  { value: 'kg', label: 'kg' },
  // Energy
  { value: 'kWh', label: 'kWh' },
  { value: 'MWh', label: 'MWh' },
  { value: 'MJ', label: 'MJ' },
  { value: 'mmBtu', label: 'mmBtu' },
  // Energy per mass
  { value: 'kWh/kg', label: 'kWh/kg' },
  { value: 'MJ/kg', label: 'MJ/kg' },
  { value: 'mmBtu/kg', label: 'mmBtu/kg' },
  { value: 'mmBtu/ton', label: 'mmBtu/ton' },
  // Power
  { value: 'kW', label: 'kW' },
  { value: 'MW', label: 'MW' },
  // Time
  { value: 'hr', label: 'hr' },
  { value: 'day', label: 'day' },
  { value: 'year', label: 'year' },
];

export const PARAM_TYPES = [
  { value: 'numeric', label: 'Numeric' },
  { value: 'boolean', label: 'Boolean' },
];

// Identifier roles → color classes for syntax highlighting + symbol legend.
export const TOKEN_COLORS = {
  output: 'text-purple-700',
  input: 'text-blue-700',
  param: 'text-emerald-700',
  function: 'text-cyan-700',
  constant: 'text-violet-700',
  keyword: 'text-rose-700 font-medium',
  number: 'text-orange-700',
  string: 'text-orange-800',
  comment: 'text-gray-400 italic',
  // identifier (let-binding or unknown), operator, whitespace: inherit
};

export const KEYWORDS = new Set(['assert']);
export const FUNCTIONS = new Set([
  'log', 'log10', 'exp', 'sqrt', 'min', 'max', 'abs',
  // Scope-aware balance helpers (must mirror SCOPE_AWARE_FUNCS in
  // core/pathway/equation_parser.py).
  'close_carbon_balance',
]);
export const CONSTANTS = new Set(['pi', 'e', 'hours_per_year', 'True', 'False']);

// Raw properties (from the species's stored property registry). Must mirror
// PROPERTY_PRIMARY_ALIASES in core/pathway/equation_parser.py.
export const PROPERTIES = ['lhv', 'hhv', 'density', 'carbon_ratio', 'molar_mass', 'pressure', 'temperature'];

// Derived accessors that auto-convert the IO's flow to a different basis,
// using the species's available properties. Must mirror DERIVED_ACCESSORS in
// core/pathway/equation_parser.py. `energy` is an alias for `energy_lhv`.
export const DERIVED_ACCESSORS = [
  'energy_lhv',
  'energy_hhv',
  'energy',
  'mass',
  'volume',
  'moles',
];

// Canonical target unit per derived accessor (display only). For energy
// accessors the *actual* preview unit is picked per-species at render
// time (mmBtu for gas, MJ for J-declared species) — these entries are
// just the fallback when no species energy unit is available.
export const DERIVED_ACCESSOR_UNITS = {
  energy: 'kWh',
  energy_lhv: 'kWh',
  energy_hhv: 'kWh',
  mass: 'kg',
  volume: 'm³',
  moles: 'mol',
};

// Energy accessors override their preview unit per-species (see ioProperties).
export const ENERGY_DERIVED_ACCESSORS = new Set(['energy', 'energy_lhv', 'energy_hhv']);
